import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'app/store';
import { getContentByUrl } from './services';
import {
  setLanguage,
  setMasterLanguage,
  setCurrentMarket,
  setUser,
  setDataLoading,
} from 'features/core/reducer';
import { getCurrentMarket } from 'common/helpers/market';
import { getPathnameFromUrl } from 'common/helpers/resolveHost';

export interface ContentState {
  [path: string]: any;
}

const initialState: ContentState = {};

/**
 * Thunk used to get page content
 * @param path
 * @param locale
 * @param isPostRequest
 * @param body
 * @param config
 * @returns
 */
export const getPageContent =
  (
    path: string,
    locale: string,
    isPostRequest?: boolean,
    body?: any,
    config?: any,
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      // Check is data from the root page is already stored
      // Root paga data is stored in state with key '/' or '/en'
      const isLocaleCoreSettingsExist = !!getState().content[locale];
      // Set current language
      dispatch(setLanguage(locale));
      // Check is requested url is not equal to root page url
      if (path !== locale) {
        // Display data loader
        dispatch(setDataLoading(true));
        // Request for the page content
        const pageResponse = await getContentByUrl(
          path,
          isPostRequest,
          body,
          config,
        );
        const { request, data } = pageResponse;
        // We're use pathname instead of url as a key
        const responsePath = getPathnameFromUrl(request?.responseURL);
        // Add page content to store
        dispatch(contentSlice.actions.setContent({ [responsePath]: data }));
        // Set active user
        dispatch(setUser(data?.LayoutModel?.ActiveProfile));
        // Hide data loader
        dispatch(setDataLoading(false));
      }
      // Fetch content from root page if is not already in store
      if (!isLocaleCoreSettingsExist) {
        const localeSettings = await getContentByUrl(locale);
        const { data } = localeSettings;
        const currentMarket = getCurrentMarket(data?.PageData);
        // Add root page cotnent to store
        dispatch(contentSlice.actions.setContent({ [locale]: data }));
        // Set master language
        dispatch(setMasterLanguage(data?.PageData?.MasterLanguage));
        // Set current marker
        dispatch(setCurrentMarket(currentMarket));
      }
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Thunk used for refresh content without whole page reload
   * @returns
   */
export const refreshContent = (): AppThunk => async (dispatch) => {
  try {
    const { pathname, search } = window.location;
    const url = pathname + search;
    dispatch(setDataLoading(true));
    const response = await getContentByUrl(url);
    if (response) {
      const { request, data } = response;
      const responsePath = getPathnameFromUrl(request.responseURL);
      dispatch(contentSlice.actions.setContent({ [responsePath]: data }));
    }
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setDataLoading(false));
  }
};

export const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    setContent(state, action: PayloadAction<any>) {
      return { ...state, ...action.payload };
    },
  },
});

export const { setContent } = contentSlice.actions;

export default contentSlice.reducer;
